import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getVisitorId} from '../utils/optimizely-visitor-id'
import {useOptimizelyFxConfig} from './use-optimizely-fx-config'
import {useSsrFetchOrigin} from './use-ssr-fetch-origin'

const REQUEST_TIMEOUT_MS = 2000
const PENDING = Object.freeze({status: 'pending', decisions: {}})
const UNAVAILABLE = Object.freeze({status: 'unavailable', decisions: {}})

/*
 * This shopper's A/B variations, resolved ONCE per session by the uncached
 * /optimizely-decisions endpoint (events disabled there — impressions fire on
 * render). Called from AudienceProvider only. Never runs during SSR; identity
 * rides in headers, never the URL; waits for the customer groups to settle so a
 * logged-in shopper is bucketed once, not twice.
 *
 * Returns {status: 'pending' | 'resolved' | 'unavailable', decisions, knownFlagKeys}.
 * `unavailable` = will not resolve this session (switch off, no visitor id,
 * endpoint or SDK down) → the filter fails open.
 */
export const useOptimizelyDecisions = ({hydrated, isLoggedIn, customerGroupsCsv, settled}) => {
    const {enabled, brandSlug, localeId} = useOptimizelyFxConfig()
    const {token: accessToken} = useAccessToken()
    const fetchOrigin = useSsrFetchOrigin()

    const visitorId = hydrated ? getVisitorId() : null
    const canResolve = hydrated && enabled && settled && !!visitorId

    const query = useQuery({
        queryKey: [
            'optimizely-decisions',
            brandSlug,
            localeId,
            visitorId,
            isLoggedIn,
            customerGroupsCsv
        ],
        queryFn: async () => {
            const controller = new AbortController()
            const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
            try {
                const params = new URLSearchParams({brand: brandSlug, locale: localeId})
                const headers = {
                    'x-visitor-id': visitorId,
                    'x-is-logged-in': isLoggedIn ? 'true' : 'false'
                }
                if (accessToken) headers.authorization = `Bearer ${accessToken}`
                if (customerGroupsCsv) headers['x-customer-groups'] = customerGroupsCsv

                const res = await fetch(`${fetchOrigin}/optimizely-decisions?${params}`, {
                    headers,
                    signal: controller.signal
                })
                if (!res.ok) throw new Error(`optimizely-decisions ${res.status}`)
                return res.json()
            } finally {
                clearTimeout(timer)
            }
        },
        enabled: canResolve,
        staleTime: Infinity,
        cacheTime: 30 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false
    })

    const {data, isError, isSuccess} = query
    return useMemo(() => {
        if (!hydrated) return PENDING
        if (!enabled || !visitorId || isError) return UNAVAILABLE
        if (!settled || !isSuccess) return PENDING
        if (!data || data.enabled === false || data.degraded) return UNAVAILABLE
        return {
            status: 'resolved',
            decisions: data.decisions || {},
            knownFlagKeys: Array.isArray(data.knownFlagKeys) ? data.knownFlagKeys : undefined
        }
    }, [hydrated, enabled, visitorId, settled, isError, isSuccess, data])
}
